import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Product } from '@/services/products.service';
import { useProductEdit } from '@/contexts/ProductEditContext';
import { useState } from 'react';
import TagInput from '@/components/ui/tag-input';
import { useTranslation } from 'react-i18next';
import { TranslationDialog, TranslationButton } from '@/components/dialogs/TranslationDialog';
import { LanguageSelector } from '@/components/ui/language-selector';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface ProductOverviewTabProps {
  product: Product;
  onProductChange?: (updates: Partial<Product>) => void;
}

export default function ProductOverviewTab({ product, onProductChange }: ProductOverviewTabProps) {
  const { t } = useTranslation();
  const { updateField, updateNestedField, productChanges, originalProduct } = useProductEdit();
  
  // Get current product data (original + changes)
  const currentProduct = originalProduct ? { ...originalProduct, ...productChanges } : product;
  
  // Map translations from API format to component format
  const nameTranslations = currentProduct.translations?.name || currentProduct.name_translations;
  const descriptionTranslations = currentProduct.translations?.description || currentProduct.description_translations;
  const [showNameTranslations, setShowNameTranslations] = useState(false);
  const [showDescriptionTranslations, setShowDescriptionTranslations] = useState(false);

  const handleChange = (field: keyof Product, value: unknown) => {
    updateField(field, value as Product[keyof Product]);
    onProductChange?.({ [field]: value });
  };

  const handleRateLimitChange = (field: string, value: string) => {
    const numValue = value === '' ? undefined : parseInt(value, 10);
    updateNestedField('rate_limits', field, numValue);
    onProductChange?.({ rate_limits: { ...currentProduct.rate_limits, [field]: numValue } });
  };

  const handleNameTranslations = (translations: { [locale: string]: string }) => {
    updateField('name_translations', translations);
    onProductChange?.({ name_translations: translations });
  };

  const handleDescriptionTranslations = (translations: { [locale: string]: string }) => {
    updateField('description_translations', translations);
    onProductChange?.({ description_translations: translations });
  };
  
  const handleSupportedLanguagesChange = (languages: string[]) => {
    updateField('supported_languages', languages);
    onProductChange?.({ supported_languages: languages });
  };
  
  const getTranslationStatus = (translations?: { [locale: string]: string }, supportedLanguages?: string[]) => {
    if (!supportedLanguages || supportedLanguages.length <= 1) {
      return 'none';
    }
    
    // Count how many languages have translations
    const translatedCount = supportedLanguages.filter(lang => 
      translations && translations[lang] && translations[lang].trim() !== ''
    ).length;
    
    // If all languages have translations, it's complete
    if (translatedCount === supportedLanguages.length) {
      return 'complete';
    }
    
    // If not all languages have translations (including when no translations exist),
    // it's incomplete
    if (translatedCount < supportedLanguages.length) {
      return 'incomplete';
    }
    
    return 'none';
  };
  
  const showTranslationButtons = currentProduct.supported_languages && currentProduct.supported_languages.length > 1;
  const currentVisibility = currentProduct.visibility ?? 'public';
  return (
    <div className="space-y-6">
      {/* Basic Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('products.productInformation')}</CardTitle>
          <CardDescription>{t('products.basicDetailsAboutProduct')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-muted-foreground">{t('common.name')}</label>
              {showTranslationButtons && (
                <TranslationButton
                  onClick={() => setShowNameTranslations(true)}
                  translationStatus={getTranslationStatus(nameTranslations, currentProduct.supported_languages)}
                />
              )}
            </div>
            <Input
              value={currentProduct.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('products.productNamePlaceholder')}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('common.version')}</label>
              <Input
                className="mt-1"
                value={currentProduct.version}
                onChange={(e) => handleChange('version', e.target.value)}
                placeholder="1.0.0"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('common.status')}</label>
              <Select
                value={currentProduct.status}
                onValueChange={(value) => handleChange('status', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">{t('products.draft')}</SelectItem>
                  <SelectItem value="published">{t('products.published')}</SelectItem>
                  <SelectItem value="archived">{t('products.archived')}</SelectItem>
                  <SelectItem value="suspended">{t('products.suspended')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('products.productVisibilityLabel')}</label>
              <Select
                value={currentVisibility}
                onValueChange={(value) => handleChange('visibility', value as Product['visibility'])}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="public">{t('products.visibilityOptions.public')}</SelectItem>
                  <SelectItem value="private">{t('products.visibilityOptions.private')}</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-muted-foreground">
                {currentVisibility === 'private'
                  ? t('products.visibilityNotes.private')
                  : t('products.visibilityNotes.public')}
              </p>
              {currentVisibility === 'private' ? (
                <Alert className="mt-2" variant="destructive">
                  <AlertDescription>{t('products.visibilityWarnings.privateCheckout')}</AlertDescription>
                </Alert>
              ) : (
                <Alert className="mt-2" variant="default">
                  <AlertDescription>{t('products.visibilityWarnings.publicCheckout')}</AlertDescription>
                </Alert>
              )}
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('common.endpoints')}</label>
              <p className="text-lg font-medium">{product.endpoint_count}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('products.authentication')}</label>
              <Select
                value={currentProduct.authentication_type || 'api_key'}
                onValueChange={(value) => handleChange('authentication_type', value)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="api_key">{t('products.authTypes.apiKey', 'API Key')}</SelectItem>
                  <SelectItem value="oauth2">{t('products.authTypes.oauth2', 'OAuth 2.0')}</SelectItem>
                  <SelectItem value="bearer">{t('products.authTypes.bearer', 'Bearer Token')}</SelectItem>
                  <SelectItem value="basic">{t('products.authTypes.basic', 'Basic Auth')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-muted-foreground">{t('common.description')}</label>
              {showTranslationButtons && (
                <TranslationButton
                  onClick={() => setShowDescriptionTranslations(true)}
                  translationStatus={getTranslationStatus(descriptionTranslations, currentProduct.supported_languages)}
                />
              )}
            </div>
            <Textarea
              value={currentProduct.description ?? ''}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={t('products.descriptionPlaceholder')}
              rows={3}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground">{t('common.tags')}</label>
            <div className="mt-1">
              <TagInput
                value={currentProduct.tags || []}
                onChange={(tags) => handleChange('tags', tags)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rate Limits */}
      {product.rate_limits && (
        <Card>
          <CardHeader>
            <CardTitle>{t('products.rateLimits')}</CardTitle>
            <CardDescription>{t('products.configureAuth')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('products.perMinute')}</label>
                <Input
                  className="mt-1"
                  type="number"
                  value={currentProduct.rate_limits?.requests_per_minute || ''}
                  onChange={(e) => handleRateLimitChange('requests_per_minute', e.target.value)}
                  placeholder="60"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('products.perHour')}</label>
                <Input
                  className="mt-1"
                  type="number"
                  value={currentProduct.rate_limits?.requests_per_hour || ''}
                  onChange={(e) => handleRateLimitChange('requests_per_hour', e.target.value)}
                  placeholder="1000"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('products.perDay')}</label>
                <Input
                  className="mt-1"
                  type="number"
                  value={currentProduct.rate_limits?.requests_per_day || ''}
                  onChange={(e) => handleRateLimitChange('requests_per_day', e.target.value)}
                  placeholder="10000"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Usage Statistics */}
      {product.usage_stats && (
        <Card>
          <CardHeader>
            <CardTitle>{t('products.usageStatistics')}</CardTitle>
            <CardDescription>{t('products.currentUsageMetrics')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('products.totalCalls')}</label>
                <p className="text-2xl font-bold">{product.usage_stats.total_calls.toLocaleString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('products.last24Hours')}</label>
                <p className="text-2xl font-bold">{product.usage_stats.last_24h.toLocaleString()}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('products.activeCustomers')}</label>
                <p className="text-2xl font-bold">{product.usage_stats.active_customers}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Language Configuration */}
      <LanguageSelector
        selectedLanguages={currentProduct.supported_languages || ['en']}
        onChange={handleSupportedLanguagesChange}
        defaultLanguage="en"
      />

      {/* Translation Dialogs */}
      <TranslationDialog
        open={showNameTranslations}
        onOpenChange={setShowNameTranslations}
        value={currentProduct.name}
        translations={nameTranslations}
        onChange={handleNameTranslations}
        fieldType="input"
        label={t('common.name')}
        supportedLanguages={currentProduct.supported_languages || ['en']}
      />

      <TranslationDialog
        open={showDescriptionTranslations}
        onOpenChange={setShowDescriptionTranslations}
        value={currentProduct.description}
        translations={descriptionTranslations}
        onChange={handleDescriptionTranslations}
        fieldType="textarea"
        label={t('common.description')}
        supportedLanguages={currentProduct.supported_languages || ['en']}
      />
    </div>
  );
}
